/**
 * Reconciliation — the bank statement against the deposit queue (ADR-044).
 *
 * Upload the bank's CSV export; the server matches its credit lines against
 * players' UTR claims and reports both directions — bank lines nobody claims,
 * and claims no bank line backs.
 *
 * **The matcher proposes. It never credits.** Nothing on this screen moves
 * money: a matched row is a proposal to walk over to the Deposits screen and
 * confirm it there, where crediting is a named operator's audited act with
 * one ledger row behind it. That separation is ADR-014's, and this screen
 * exists to make the by-eye statement search unnecessary without moving the
 * settlement authority an inch.
 */
import { useRef, useState, type ReactNode } from 'react';
import { fetchReconciliationReport, importBankStatement } from '../api/client.ts';
import type {
  ReconBankRow,
  ReconciliationReport,
  ReconImportResult,
  ReconMatch,
  ReconUnclaimedDeposit,
} from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age } from '../lib/datetime.ts';
import { navigate } from '../lib/router.ts';
import { currentOperator } from '../api/auth.ts';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Money,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useToast } from '../ui/feedback.tsx';
import { IconAlert, IconInbound, IconPlug } from '../ui/icons.tsx';

export function Reconciliation(): ReactNode {
  const report = useAsync(() => fetchReconciliationReport(), []);
  const [lastImport, setLastImport] = useState<ReconImportResult | null>(null);

  return (
    <div className="stack">
      <ImportCard
        onImported={(result) => {
          setLastImport(result);
          report.reload();
        }}
      />
      <ReportView
        report={lastImport?.report ?? report.data}
        loading={report.loading && !lastImport}
        error={lastImport ? null : report.error}
        onRetry={report.reload}
      />
    </div>
  );
}

// ------------------------------------------------------------------- import

function ImportCard({ onImported }: { onImported: (r: ReconImportResult) => void }): ReactNode {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState<ReconImportResult['skipped']>([]);
  const isAdmin = currentOperator()?.role === 'admin';

  async function pick(file: File | undefined): Promise<void> {
    if (!file) return;
    setFileName(file.name);
    setCsv(await file.text());
  }

  async function run(): Promise<void> {
    if (!csv) return;
    setBusy(true);
    try {
      const result = await importBankStatement(csv, label || undefined);
      setSkipped(result.skipped);
      toast(
        `Imported ${result.imported} credit line${result.imported === 1 ? '' : 's'}` +
          (result.duplicates > 0 ? ` · ${result.duplicates} already known` : '') +
          (result.skipped.length > 0 ? ` · ${result.skipped.length} skipped` : ''),
      );
      setCsv(null);
      setFileName(null);
      if (fileRef.current) fileRef.current.value = '';
      onImported(result);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'The import failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Import a bank statement"
      sub="CSV export from net banking — HDFC, ICICI, SBI, Axis and Kotak shapes are understood. Credit lines only; the matcher proposes and never credits"
      icon={<IconInbound />}
    >
      <div className="stack">
        <div className="toolbar">
          <input
            ref={fileRef}
            className="input"
            type="file"
            accept=".csv,text/csv,text/plain"
            disabled={!isAdmin || busy}
            onChange={(e) => void pick(e.target.files?.[0])}
          />
          <input
            className="input"
            value={label}
            placeholder="Label, e.g. HDFC 26 Aug (optional)"
            maxLength={120}
            disabled={!isAdmin || busy}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={!isAdmin || busy || !csv}
            onClick={() => void run()}
          >
            {busy ? 'Importing…' : fileName ? `Import ${fileName}` : 'Import'}
          </button>
        </div>
        {!isAdmin ? (
          <span className="field__hint">
            Importing a statement is an admin action. You can read the report below.
          </span>
        ) : (
          <span className="field__hint">
            A statement line is matched at most once — re-importing an overlapping export is
            counted as duplicates, never doubled. Confirming a matched deposit stays on the
            Deposits screen.
          </span>
        )}
        {skipped.length > 0 ? (
          <div className="banner banner--warn">
            <span className="banner__icon">
              <IconAlert />
            </span>
            <div className="banner__body">
              <div className="banner__title">
                {skipped.length} line{skipped.length === 1 ? '' : 's'} skipped
              </div>
              <ul className="banner__list">
                {skipped.slice(0, 6).map((s) => (
                  <li key={s.line}>
                    line {s.line} — {s.reason}
                  </li>
                ))}
                {skipped.length > 6 ? <li>… and {skipped.length - 6} more</li> : null}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------- report

function ReportView({
  report,
  loading,
  error,
  onRetry,
}: {
  report: ReconciliationReport | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}): ReactNode {
  if (error) {
    return (
      <Card title="Reconciliation report" icon={<IconPlug />}>
        <ErrorState title="The report did not load" detail={error} onRetry={onRetry} />
      </Card>
    );
  }
  if (!report) {
    return (
      <Card title="Reconciliation report" icon={<IconPlug />} flush>
        {loading ? (
          <TableSkeleton columns={['text', 'text', 'num', 'num']} rows={6} />
        ) : (
          <EmptyState
            compact
            icon={<IconPlug size="lg" />}
            title="No statement imported yet"
            hint="Import a CSV above; the report compares its credit lines with players' UTR claims."
          />
        )}
      </Card>
    );
  }

  return (
    <>
      <MatchTable
        title="Matched — ready to confirm"
        sub="A pending deposit claims this exact UTR. Confirm it on the Deposits screen; nothing here credits"
        rows={report.matched}
        emptyTitle="No open matches"
        emptyHint="Every imported credit line is either settled, unclaimed, or waiting for a player's UTR claim."
        action
      />
      <div className="split">
        <UnmatchedTable rows={report.unmatchedBankRows} />
        <UnclaimedTable rows={report.unclaimedDeposits} />
      </div>
      <MatchTable
        title="Settled"
        sub="Bank lines whose UTR a deposit has already been credited with — the trail, both ends tied"
        rows={report.settled}
        emptyTitle="Nothing settled against this statement yet"
      />
      <span className="field__hint num">
        {report.bankRows.toLocaleString()} statement lines on record · report generated{' '}
        {absolute(report.generatedAt)} — recomputed on every load, never cached
      </span>
    </>
  );
}

function MatchTable({
  title,
  sub,
  rows,
  emptyTitle,
  emptyHint,
  action,
}: {
  title: string;
  sub?: string;
  rows: ReconMatch[];
  emptyTitle: string;
  emptyHint?: string;
  action?: boolean;
}): ReactNode {
  return (
    <Card title={title} {...(sub === undefined ? {} : { sub })} icon={<IconPlug />} count={rows.length} flush>
      {rows.length === 0 ? (
        <EmptyState compact icon={<IconPlug size="lg" />} title={emptyTitle} {...(emptyHint === undefined ? {} : { hint: emptyHint })} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Bank date</th>
                <th>UTR</th>
                <th className="right">Bank amount</th>
                <th>Deposit</th>
                <th>Player</th>
                <th className="right">Deposit amount</th>
                <th>Amounts</th>
                {action ? <th className="right" aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.rowId}>
                  <td className="mono nowrap">{m.bankDate ?? '—'}</td>
                  <td className="mono nowrap">{m.utr}</td>
                  <td className="right mono">
                    <Money value={m.amount} />
                  </td>
                  <td className="mono dim nowrap">#{m.depositId}</td>
                  <td className="nowrap cell-lead">
                    {m.username}
                    {m.claimantCount > 1 ? (
                      <>
                        {' '}
                        <Badge tone="pending">{m.claimantCount} claims</Badge>
                      </>
                    ) : null}
                  </td>
                  <td className="right mono dim">
                    <Money value={m.depositAmount} />
                  </td>
                  <td>
                    {m.amountMatches ? (
                      <Badge tone="win">match</Badge>
                    ) : (
                      <Badge tone="loss">differ</Badge>
                    )}
                  </td>
                  {action ? (
                    <td className="right">
                      <button
                        type="button"
                        className="btn btn--sm"
                        title="Confirming stays on the Deposits screen — the matcher never credits"
                        onClick={() => navigate('deposits')}
                      >
                        Open in Deposits
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function UnmatchedTable({ rows }: { rows: ReconBankRow[] }): ReactNode {
  return (
    <Card
      title="Bank lines nobody claims"
      sub="Money arrived; no pending deposit claims its UTR"
      icon={<IconAlert />}
      count={rows.length}
      flush
    >
      {rows.length === 0 ? (
        <EmptyState compact icon={<IconPlug size="lg" />} title="Every credit line is accounted for" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Bank date</th>
                <th>UTR</th>
                <th>Narration</th>
                <th className="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rowId}>
                  <td className="mono nowrap">{r.bankDate ?? '—'}</td>
                  <td className="mono nowrap">{r.utr ?? <span className="dim">none</span>}</td>
                  <td className="dim" title={r.description ?? ''}>
                    {r.description ?? '—'}
                  </td>
                  <td className="right mono">
                    <Money value={r.amount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function UnclaimedTable({ rows }: { rows: ReconUnclaimedDeposit[] }): ReactNode {
  return (
    <Card
      title="Claims with no bank line"
      sub="The player says they paid; this statement does not show it"
      icon={<IconAlert />}
      count={rows.length}
      flush
    >
      {rows.length === 0 ? (
        <EmptyState compact icon={<IconPlug size="lg" />} title="No unbacked claims" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Deposit</th>
                <th>Player</th>
                <th>Claimed UTR</th>
                <th className="right">Amount</th>
                <th className="right">Claimed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.depositId}>
                  <td className="mono dim nowrap">#{d.depositId}</td>
                  <td className="nowrap cell-lead">{d.username}</td>
                  <td className="mono nowrap">{d.claimedUtr}</td>
                  <td className="right mono">
                    <Money value={d.amount} />
                  </td>
                  <td className="right mono muted" title={absolute(d.utrClaimedAt)}>
                    {age(d.utrClaimedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
